import React, { useState } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'
import { toast } from 'react-toastify'

export default function AdminChangePassword() {
    const { token } = useAuth()
    const [form, setForm] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' })
    const [loading, setLoading] = useState(false)

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm(p => ({ ...p, [e.target.name]: e.target.value }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token) { toast.error('Authentication required'); return }
        if (form.newPassword !== form.confirmPassword) { toast.error('Security override: Passwords do not match'); return }
        if (form.newPassword.length < 6) { toast.error('Protocol error: Password must be at least 6 characters'); return }
        
        setLoading(true)
        try {
            const res = await api.changePassword(token, { oldPassword: form.oldPassword, newPassword: form.newPassword })
            if (res.message === 'Password changed successfully') {
                toast.success('Password updated successfully')
                setForm({ oldPassword: '', newPassword: '', confirmPassword: '' })
            } else {
                toast.error(res.message || 'Update failed')
            }
        } catch (error) {
            toast.error('Server error')
        } finally {
            setLoading(false)
        }
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Change Password</h1>
                    <p className="text-slate-500 font-medium text-base">Update your administrative login credentials.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
                <div className="lg:col-span-12">
                    <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-xl overflow-hidden relative max-w-4xl">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-rose-50/30 rounded-none blur-[80px] -mr-32 -mt-32 pointer-events-none"></div>
                        
                        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2">
                            {/* Form side */}
                             <div className="p-8 border-r border-slate-100 dark:border-slate-800">
                                <div className="mb-8">
                                    <h3 className="text-xl font-bold text-slate-900 dark:text-white">Update Password</h3>
                                    <p className="text-sm font-medium text-slate-500 mt-1">Change your account password</p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                     <div className="space-y-2">
                                        <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">Current Password</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                                                <i className="fa-solid fa-lock text-sm"></i>
                                            </div>
                                            <input
                                                type="password"
                                                name="oldPassword"
                                                value={form.oldPassword}
                                                onChange={handleChange}
                                                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl pl-12 pr-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                                placeholder="Enter current password"
                                                required
                                            />
                                        </div>
                                    </div>

                                     <div className="space-y-2">
                                        <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">New Password</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                                                <i className="fa-solid fa-key text-sm"></i>
                                            </div>
                                            <input
                                                type="password"
                                                name="newPassword"
                                                value={form.newPassword}
                                                onChange={handleChange}
                                                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl pl-12 pr-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                                placeholder="Enter new password"
                                                required
                                            />
                                        </div>
                                    </div>

                                     <div className="space-y-2">
                                        <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">Confirm Password</label>
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400">
                                                <i className="fa-solid fa-shield-halved text-sm"></i>
                                            </div>
                                            <input
                                                type="password"
                                                name="confirmPassword"
                                                value={form.confirmPassword}
                                                onChange={handleChange}
                                                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl pl-12 pr-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                                placeholder="Confirm new password"
                                                required
                                            />
                                        </div>
                                    </div>

                                     <button 
                                        type="submit" 
                                        disabled={loading}
                                        className="w-full py-3.5 bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-50 rounded-xl text-sm font-bold shadow-md transition-all disabled:opacity-50 mt-4 active:scale-95"
                                    >
                                        {loading ? 'Updating...' : 'Update Password'}
                                    </button>
                                </form>
                            </div>

                            {/* Requirements side */}
                             <div className="p-8 bg-slate-50 dark:bg-slate-800/30 flex flex-col justify-center">
                                <h4 className="text-sm font-bold text-slate-900 dark:text-blue-400 mb-6 uppercase tracking-wider">Password Requirements</h4>
                                <ul className="space-y-4">
                                    <li className="flex items-start gap-3">
                                        <i className="fa-solid fa-circle-check text-emerald-500 text-xs mt-1"></i>
                                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Minimum 6 characters long</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <i className="fa-solid fa-circle-check text-emerald-500 text-xs mt-1"></i>
                                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Include at least one uppercase letter</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <i className="fa-solid fa-circle-check text-emerald-500 text-xs mt-1"></i>
                                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Include at least one digit</span>
                                    </li>
                                    <li className="flex items-start gap-3">
                                        <i className="fa-solid fa-circle-check text-emerald-500 text-xs mt-1"></i>
                                        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Must match exactly in both fields</span>
                                    </li>
                                </ul>

                                 <div className="mt-10 p-5 bg-blue-50/50 border border-blue-100 rounded-xl">
                                    <p className="text-xs font-semibold text-blue-800 leading-relaxed">
                                        <i className="fa-solid fa-circle-info text-blue-600 mr-2"></i>
                                        Note: Changing your password will sign you out from all other active sessions for security.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    )
}


